import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BellRinging, X } from "@phosphor-icons/react";
import { makeWalkApi, roomLabel, roomPhotos } from "@/components/walk/walkApi";
import { api as httpApi, formatApiErrorDetail } from "@/lib/api";
import RoomListScreen from "@/components/walk/RoomListScreen";
import RoomScreen from "@/components/walk/RoomScreen";
import BurstCamera from "@/components/walk/BurstCamera";
import BlindsSheet from "@/components/walk/BlindsSheet";
import NameplateSheet from "@/components/walk/NameplateSheet";
import FieldRadarLogo from "@/components/walk/FieldRadarLogo";
import BatchPhotoImport from "@/components/BatchPhotoImport";
import SortBoard from "@/components/walk/SortBoard";
import ReviewScreen from "@/components/walk/ReviewScreen";

/**
 * WalkV2 — InspectRadar (staff, JWT) + TenantRadar (tenant, magic link) walk UI.
 * Screens: room list → one room per screen → burst camera / blinds sheet / nameplate sheet.
 */
export default function WalkV2({ mode = "staff", id, token, onExit }) {
  const nav = useNavigate();
  const api = useRef(makeWalkApi({ mode, id, token })).current;
  const [data, setData] = useState(null);
  const [roomIdx, setRoomIdx] = useState(null);
  const [walkMode, setWalkMode] = useState("guided");
  const [allowLibrary, setAllowLibraryState] = useState(null);
  const [camera, setCamera] = useState(null);       // {item, resolve?}
  const [blinds, setBlinds] = useState(null);       // item name
  const [plate, setPlate] = useState(null);         // item name
  const [notifyDismissed, setNotifyDismissed] = useState(false);
  const [importing, setImporting] = useState(false);
  const [sorting, setSorting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const pendingPhoto = useRef(null);

  const load = async () => {
    try { const d = await api.load(); setData(d); setWalkMode(d.walk_mode || "guided"); setAllowLibraryState(d.allow_library ?? null); }
    catch { toast.error("Couldn't load this walk"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Optimistic local patch helper so the UI never waits on the network
  const patchRoom = (idx, fn) => setData(d => ({ ...d, rooms: d.rooms.map((r, i) => i === idx ? fn(r) : r) }));

  if (!data) return <div className="min-h-screen bg-[#0b1c2d] text-slate-300 flex flex-col items-center justify-center gap-3 font-bold"><FieldRadarLogo size={34}/>Loading walk…</div>;
  const rooms = data.rooms || [];
  const room = roomIdx != null ? rooms[roomIdx] : null;
  const staff = mode === "staff";

  const libraryAllowed = allowLibrary == null ? staff : allowLibrary;
  const setMode = async (m) => { setWalkMode(m); if (staff) api.room({ room_idx: 0, mode: m }).catch(() => {}); };

  const markStatus = async (idx, status) => {
    patchRoom(idx, r => ({ ...r, status }));
    await api.room({ room_idx: idx, status }).catch(() => toast.error("Couldn't save room status"));
  };

  const openRoom = (idx) => { setRoomIdx(idx); if (rooms[idx].status === "wait") markStatus(idx, "prog"); };
  const nextRoom = async (override = false) => {
    if (override) { patchRoom(roomIdx, r => ({ ...r, status: "done", override: true })); await api.room({ room_idx: roomIdx, status: "done", override: true }).catch(() => {}); }
    else await markStatus(roomIdx, "done");
    if (roomIdx + 1 < rooms.length) setRoomIdx(roomIdx + 1); else setRoomIdx(null);
  };

  const onShot = async (blob, { takenAt, item }) => {
    const r = await api.photo(roomIdx, blob, { item, takenAt });
    patchRoom(roomIdx, rm => ({ ...rm, status: rm.status === "wait" ? "prog" : rm.status,
      steps: [{ ...(rm.steps?.[0] || { kind: "photos" }), photos: [...roomPhotos(rm), r.photo] }, ...(rm.steps || []).slice(1)] }));
    if (pendingPhoto.current) { pendingPhoto.current(r.photo); pendingPhoto.current = null; setCamera(null); }
    return r;
  };

  // Sheets ask for a single photo: open camera, resolve on first shot
  const singlePhoto = (item) => new Promise(resolve => {
    pendingPhoto.current = resolve;
    setCamera({ item, single: true, resolve });
  });

  const saveCallouts = async (path, callouts) => {
    try {
      const r = await api.callouts({ room_idx: roomIdx, path, callouts });
      patchRoom(roomIdx, rm => ({ ...rm, steps: [{ ...(rm.steps?.[0] || {}), photos: roomPhotos(rm).map(p => p.path === path ? { ...p, callouts: r.callouts } : p) }, ...(rm.steps || []).slice(1)] }));
      toast.success(`${r.callouts.length} call-out${r.callouts.length === 1 ? "" : "s"} saved`);
    } catch { toast.error("Couldn't save call-outs"); }
  };

  const deletePhoto = async (path) => {
    patchRoom(roomIdx, rm => ({ ...rm, steps: [{ ...(rm.steps?.[0] || {}), photos: roomPhotos(rm).filter(p => p.path !== path) }, ...(rm.steps || []).slice(1)] }));
    try { await api.deletePhoto({ room_idx: roomIdx, path }); toast.success("Photo deleted"); }
    catch { toast.error("Couldn't delete photo"); load(); }
  };

  const rate = async (item, rating) => {
    patchRoom(roomIdx, r => ({ ...r, items: r.items.map(it => it.name === item ? { ...it, rating } : it) }));
    await api.item({ room_idx: roomIdx, item, rating }).catch(() => toast.error("Couldn't save rating"));
  };

  const saveMeasure = async (item, body) => {
    const r = await api.measure({ room_idx: roomIdx, item, ...body });
    patchRoom(roomIdx, rm => ({ ...rm, measurements: { ...(rm.measurements || {}), [item]: r.measurement } }));
    toast.success(`Saved ${body.width_in ?? "?"}″ × ${body.height_in ?? "?"}″ for the turn crew`);
  };

  const saveNameplate = async (item, body) => {
    await api.nameplate({ room_idx: roomIdx, item, ...body });
    patchRoom(roomIdx, rm => ({ ...rm, appliances: { ...(rm.appliances || {}), [item.replace(/[./]/g, "_")]: body } }));
    toast.success(`${item} saved on the unit's assets`);
  };

  const roomNote = async (text) => {
    patchRoom(roomIdx, r => ({ ...r, note: text }));
    await api.room({ room_idx: roomIdx, note: text }).catch(() => toast.error("Couldn't save note"));
  };

  const fastFlag = async (text) => {
    patchRoom(roomIdx, r => ({ ...r, fast_flag: text || null }));
    await api.room({ room_idx: roomIdx, fast_flag: text }).catch(() => {});
  };

  const findingWo = async (item, ri = roomIdx) => {
    try {
      const r = await api.findingWo({ room_idx: ri, item });
      patchRoom(ri, rm => item
        ? { ...rm, items: rm.items.map(it => it.name === item ? { ...it, wo_id: r.wo_id } : it) }
        : { ...rm, fast_flag_wo_id: r.wo_id });
      toast.success("Work order created with the photos attached");
    } catch { toast.error("Couldn't create work order"); }
  };

  const assignUnsorted = async (paths, roomIdx) => {
    try {
      await api.assignUnsorted({ paths, room_idx: roomIdx });
      const moved = (data.unsorted_photos || []).filter(p => paths.includes(p.path));
      setData(d => ({ ...d, unsorted_photos: (d.unsorted_photos || []).filter(p => !paths.includes(p.path)),
        rooms: d.rooms.map((r, i) => i === roomIdx ? { ...r, status: r.status === "wait" ? "prog" : r.status,
          steps: [{ ...(r.steps?.[0] || {}), photos: [...roomPhotos(r), ...moved] }, ...(r.steps || []).slice(1)] } : r) }));
      toast.success(`Moved ${paths.length} → ${roomLabel(rooms[roomIdx])}`);
    } catch { toast.error("Couldn't move photos"); }
  };
  const deleteUnsorted = async (paths) => {
    try { await api.assignUnsorted({ paths }); setData(d => ({ ...d, unsorted_photos: (d.unsorted_photos || []).filter(p => !paths.includes(p.path)) })); }
    catch { toast.error("Couldn't delete"); }
  };

  const finish = async () => {
    if (!reviewing) { setReviewing(true); return; }
    if (!staff) {
      try { await api.submit({}); toast.success("Thanks — your walk was sent to the office"); setData(d => ({ ...d, tenant_status: "submitted" })); }
      catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't submit")); }
      return;
    }
    const left = rooms.filter(r => r.status !== "done").length;
    if (!window.confirm(left ? `${left} room${left === 1 ? "" : "s"} not marked Done.\n\nFinish and submit anyway (override)?` : "Finish and submit this walk?")) return;
    try { await api.submit({ signer_name: null }); toast.success("Walk submitted"); (onExit || (() => nav(`/admin/property-inspections/${id}`)))(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Couldn't submit")); }
  };

  const notifyTenant = async () => {
    try {
      const r = await api.tenantLink();
      await navigator.clipboard?.writeText(r.url).catch(() => {});
      toast.success("Tenant link ready — copied. Send it by text so they know you're walking today.");
    } catch { toast.error("Couldn't create tenant link"); }
    setNotifyDismissed(true);
  };
  const notifyCard = staff && data.kind === "periodic" && !notifyDismissed && data.status !== "submitted" ? (
    <div className="mx-4 mt-3 p-3 rounded-2xl bg-teal-500/10 border border-teal-400/30 flex items-center gap-3" data-testid="walk-notify-card">
      <BellRinging weight="fill" size={24} className="text-teal-300 shrink-0"/>
      <div className="flex-1 text-sm"><b>Periodic walk</b> — let the tenant know?</div>
      <button onClick={notifyTenant} className="h-10 px-3 rounded-xl bg-teal-500 text-slate-900 font-black text-xs" data-testid="walk-notify-tenant">Notify tenant</button>
      <button onClick={() => setNotifyDismissed(true)} className="h-10 px-3 rounded-xl bg-white/10 font-bold text-xs" data-testid="walk-notify-skip">Skip</button>
    </div>
  ) : null;

  if (!staff && data.tenant_status === "submitted") {
    return (
      <div className="min-h-screen bg-[#0b1c2d] text-white flex flex-col items-center justify-center p-8 text-center gap-3" data-testid="walk-tenant-done">
        <FieldRadarLogo size={40} sub="Tenant Radar"/>
        <div className="text-3xl font-black mt-4">All done — thank you!</div>
        <div className="text-slate-300">Your photos for {data.property_address} were sent to the office.</div>
      </div>
    );
  }

  return (
    <>
      {reviewing ? (
        <ReviewScreen data={data} staff={staff} onBack={() => setReviewing(false)} onComplete={finish}
          onFindingWo={(ri, item) => findingWo(item, ri)}
          onSendOwner={staff ? async () => {
            const to_email = window.prompt("Owner email (leave blank to use the landlord linked to this property):", "") ?? null;
            if (to_email === null) return;
            const message = window.prompt("Message to the owner (optional):", "") || "";
            try {
              const r = await httpApi.post(`/inspections/${id}/findings-approval/send`, { to_email, message });
              toast.success(`Sent ${r.data.findings} findings to ${r.data.to}${r.data.email_sent ? "" : " (email not delivered — link copied)"}`);
              if (!r.data.email_sent) { try { await navigator.clipboard.writeText(r.data.url); } catch {} }
            } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not send")); }
          } : undefined}/>
      ) : room == null ? (
        <RoomListScreen data={data} mode={walkMode} setMode={setMode} staff={staff} onOpen={openRoom}
          onBack={() => (onExit || (() => nav(staff ? `/admin/property-inspections/${id}` : "/")))()} onFinish={finish} notifyCard={notifyCard}
          unsortedCount={(data.unsorted_photos || []).length} onImport={() => setImporting(true)} onSort={() => setSorting(true)}/>
      ) : (
        <RoomScreen data={data} roomIdx={roomIdx} mode={walkMode} staff={staff}
          onBack={() => setRoomIdx(null)} onNext={nextRoom} onMenu={() => setRoomIdx(null)}
          onOpenCamera={(item) => setCamera({ item })} onRate={rate}
          onMeasure={(item) => setBlinds(item)} onNameplate={(item) => setPlate(item)}
          onFastFlag={fastFlag} onFindingWo={findingWo} onDeletePhoto={deletePhoto} onNote={roomNote} onCallouts={saveCallouts}/>
      )}

      {camera && room && (
        <BurstCamera title={roomLabel(room)} initialCount={roomPhotos(room).length} onShot={onShot} onDelete={deletePhoto}
          allowLibrary={libraryAllowed} stamp={!staff} itemLabel={camera.item || undefined}
          onDone={() => { if (camera.single && pendingPhoto.current) { pendingPhoto.current(null); pendingPhoto.current = null; } setCamera(null); }}/>
      )}
      {blinds && room && (
        <BlindsSheet roomName={roomLabel(room)} existing={room.measurements?.[blinds]} onClose={() => setBlinds(null)}
          onSave={(body) => saveMeasure(blinds, body)} onPhoto={() => singlePhoto(blinds)}/>
      )}
      {plate && room && (
        <NameplateSheet item={plate} roomName={roomLabel(room)} existing={room.appliances?.[plate.replace(/[./]/g, "_")]}
          onClose={() => setPlate(null)} onSave={(body) => saveNameplate(plate, body)} onPhoto={() => singlePhoto(plate)}/>
      )}
      {importing && staff && (
        <BatchPhotoImport title="Import photos" subtitle={`${data.property_address} · lands in the Unsorted tray`}
          upload={(file, meta) => api.unsorted(file, meta)} onClose={() => setImporting(false)}
          onDone={async () => { setImporting(false); await load(); setSorting(true); }}/>
      )}
      {sorting && staff && (
        <SortBoard rooms={rooms} photos={data.unsorted_photos || []} onAssign={assignUnsorted} onDelete={deleteUnsorted}
          onAiSort={async () => { toast("Claude is looking at each photo…", { duration: 3000 }); try { const r = await api.aiSort({}); setData(d => ({ ...d, unsorted_photos: r.photos })); toast.success(`${r.suggested} photos sorted`); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "AI sort failed")); } }}
          onClose={() => setSorting(false)} onImportMore={() => { setSorting(false); setImporting(true); }}/>
      )}
      <span className="hidden"><X/></span>
    </>
  );
}
